import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import { usePriceLists } from "shared-apis";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { Checkbox } from "components/Checkbox";
import { Radio } from "components/Radio";
import { Calendar } from "components/Calendar";
import { Accordion } from "components/Accordion";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace, HorizontalSpace, Divider } from "components/Spacing";

import {
  createClient,
  updateClient,
  getClientAccount,
  updateClientAccount,
} from "./api";
import { CounterFieldsGrid } from "./ClientGroupModal";
import { ClientDiscountsSection } from "./ClientDiscountsSection";
import {
  ClientBonusOperations,
  ClientTransactionsSection,
} from "./ClientBonusesSection";
import { ClientDatesSection } from "./ClientDatesSection";
import styles from "./styles.module.scss";

const ACCOUNT_DISCOUNT_FIELDS = [
  { field: "startpercent", label: "Начальный % скидки", symbol: "%" },
  { field: "shiftpercent", label: "Шаг скидки" },
  { field: "shiftpercentsum", label: "Период увеличения скидки" },
  { field: "discountpercent", label: "Текущий % скидки", symbol: "%" },
  { field: "endpercent", label: "Конечный % скидки", symbol: "%" },
  { field: "nextdiscountsum", label: "Следующее увеличение скидки" },
];

const ACCOUNT_BONUS_FIELDS = [
  { field: "startbonus", label: "Начальный % бонуса", symbol: "%" },
  { field: "shiftbonus", label: "Шаг бонуса" },
  { field: "shiftbonussum", label: "Период увеличения бонуса" },
  { field: "bonuspercent", label: "Текущий % бонуса", symbol: "%" },
  { field: "endbonus", label: "Конечный % бонуса", symbol: "%" },
  { field: "nextbonussum", label: "Следующее увеличение бонуса" },
];

const ACCOUNT_FIELDS = [...ACCOUNT_DISCOUNT_FIELDS, ...ACCOUNT_BONUS_FIELDS];

/** Карточка клиента (создание / редактирование) */
export const ClientModal = ({ isEdit, client, group }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  /* ---- основные поля ---- */
  const [firma, setFirma] = useState(String(client?.firma ?? 0));
  const [form, setForm] = useState({
    firstname: client?.firstname ?? "",
    secondname: client?.secondname ?? "",
    otc: client?.otc ?? "",
    phone: client?.phone ?? "",
    email: client?.email ?? "",
    card: client?.card ?? "",
    city: client?.city ?? "",
    address: client?.address ?? "",
    house: client?.house ?? "",
    korpus: client?.korpus ?? "",
    flat: client?.flat ?? "",
    birthday: client?.birthday ? new Date(client.birthday) : null,
    sex: String(client?.sex ?? "1"),
    inn: client?.inn ?? "",
    kpp: client?.kpp ?? "",
    bank: client?.bank ?? "",
    avaliable: Boolean(client?.avaliable ?? 1),
    notverifysms: Boolean(client?.notverifysms ?? 0),
    eachbouquetdiscount: client?.eachbouquetdiscount ?? "",
    eachbouquet: client?.eachbouquet ?? "",
    defaultburnbonus: client?.defaultburnbonus ?? "",
    comment: client?.comment ?? "",
  });
  const patch = (update) => setForm((prev) => ({ ...prev, ...update }));

  /* ---- прайслист ---- */
  const priceListsQuery = usePriceLists();
  const priceListOptions = useMemo(
    () =>
      (priceListsQuery.data?.items ?? []).map((p) => ({
        value: String(p.pricelistid),
        label: p.name,
      })),
    [priceListsQuery.data]
  );
  const [priceListOption, setPriceListOption] = useState(null);
  useEffect(() => {
    if (!priceListOptions.length) return;
    setPriceListOption(
      priceListOptions.find(
        (o) => String(o.value) === String(client?.pricelistid ?? "")
      ) || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceListOptions]);

  /* ---- лицевой счёт (только edit) ---- */
  const accountQuery = useQuery(
    ["client-account", client?.clientid],
    () => getClientAccount(client.clientid),
    { enabled: Boolean(isEdit && client?.clientid) }
  );
  const account = accountQuery.data;

  const [accountValues, setAccountValues] = useState({});
  useEffect(() => {
    if (!account) return;
    const values = {};
    ACCOUNT_FIELDS.forEach(({ field }) => {
      values[field] = account[field] ?? 0;
    });
    setAccountValues(values);
  }, [account]);

  const onAccountFieldChange = (field, value) =>
    setAccountValues((prev) => ({ ...prev, [field]: value }));

  /* ---- аккордеоны ---- */
  const [collapsed, setCollapsed] = useState({
    discounts: true,
    bonuses: true,
    transactions: true,
    marketing: true,
    info: true,
  });
  const toggle = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /* ---- сохранение ---- */
  const handleSave = async () => {
    if (!form.firstname.trim().length) {
      setError(
        'Необходимо заполнить "Наименование" (или "Имя", если выбрали физ. лицо)'
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        clientgroupid: Number(group?.clientgroupid || 0),
        firma: Number(firma),
        firstname: form.firstname,
        secondname: form.secondname,
        otc: form.otc,
        phone: form.phone,
        email: form.email,
        card: form.card,
        city: form.city,
        address: form.address,
        house: form.house,
        korpus: form.korpus,
        flat: form.flat,
        birthday: form.birthday ? form.birthday.toISOString() : null,
        sex: Number(form.sex),
        inn: form.inn,
        kpp: form.kpp,
        bank: form.bank,
        avaliable: form.avaliable ? 1 : 0,
        notverifysms: form.notverifysms ? 1 : 0,
        eachbouquetdiscount: Number(form.eachbouquetdiscount) || 0,
        eachbouquet: Number(form.eachbouquet) || 0,
        defaultburnbonus: Number(form.defaultburnbonus) || 0,
        comment: form.comment,
        pricelistid: Number(priceListOption?.value) || 0,
        streetid: Number(client?.streetid) || 0,
        createtime: client?.createtime ?? new Date().toISOString(),
      };

      if (isEdit) {
        await updateClient({ ...payload, clientid: client.clientid });
        // Лицевой счёт сохраняется вместе с клиентом
        if (account?.clientaccountid) {
          const numericAccount = {};
          Object.entries(accountValues).forEach(([key, val]) => {
            numericAccount[key] = Number(val) || 0;
          });
          await updateClientAccount({
            ...numericAccount,
            clientaccountid: account.clientaccountid,
            avaliable: 1,
            name: form.firstname,
          });
        }
      } else {
        await createClient(payload);
      }

      queryClient.invalidateQueries(["clients"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* ---- рендер ---- */
  const isIndividual = firma === "0";

  return (
    <ModalShell width={940} className={styles.clientModal}>
      <ModalHeader
        title={isEdit ? client?.firstname : "Новый клиент"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.clientTopRow}>
          <RegularText>Группа:</RegularText>
          <HorizontalSpace size={6} />
          <MediumText color={group ? "primary" : "danger"}>
            {group?.name ?? "Группа удалена"}
          </MediumText>
          <HorizontalSpace size={24} />
          <Checkbox
            label="Активен"
            checked={form.avaliable}
            onChange={(val) => patch({ avaliable: val })}
          />
          <HorizontalSpace size={24} />
          <Checkbox
            label="Не подтверждать клиента по СМС"
            checked={form.notverifysms}
            onChange={(val) => patch({ notverifysms: val })}
          />
          {isEdit && (
            <div className={styles.clientCode}>
              <Input
                label="Код"
                labelWidth={40}
                value={client?.clientid ?? ""}
                disabled
                inputWidth={140}
              />
            </div>
          )}
        </div>

        <VerticalSpace size={16} />

        <div className={styles.firmaRow}>
          <Radio
            id="firma-0"
            name="firma"
            label="Физическое лицо"
            checked={firma === "0"}
            onChange={() => setFirma("0")}
          />
          <Radio
            id="firma-1"
            name="firma"
            label="ООО"
            checked={firma === "1"}
            onChange={() => setFirma("1")}
          />
          <Radio
            id="firma-2"
            name="firma"
            label="ИП"
            checked={firma === "2"}
            onChange={() => setFirma("2")}
          />
        </div>

        <VerticalSpace size={16} />

        {isIndividual ? (
          <>
            <div className={styles.clientFieldsGrid}>
              <Input
                label="Фамилия"
                labelWidth={90}
                value={form.secondname}
                onChange={(ev) => patch({ secondname: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Имя"
                labelWidth={90}
                value={form.firstname}
                onChange={(ev) => patch({ firstname: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Отчество"
                labelWidth={90}
                value={form.otc}
                onChange={(ev) => patch({ otc: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Телефон"
                labelWidth={90}
                value={form.phone}
                onChange={(ev) => patch({ phone: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="E-mail"
                labelWidth={90}
                value={form.email}
                onChange={(ev) => patch({ email: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Карта"
                labelWidth={90}
                value={form.card}
                onChange={(ev) => patch({ card: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Город"
                labelWidth={90}
                value={form.city}
                onChange={(ev) => patch({ city: ev.target.value })}
                inputWidth={160}
              />
              <Input
                label="Улица"
                labelWidth={90}
                value={form.address}
                onChange={(ev) => patch({ address: ev.target.value })}
                inputWidth={160}
              />
            </div>
            <VerticalSpace size={12} />
            <div className={styles.addressRow}>
              <Input
                label="Д"
                labelWidth={20}
                value={form.house}
                onChange={(ev) => patch({ house: ev.target.value })}
                inputWidth={50}
              />
              <Input
                label="Кор"
                labelWidth={30}
                value={form.korpus}
                onChange={(ev) => patch({ korpus: ev.target.value })}
                inputWidth={50}
              />
              <Input
                label="Кв"
                labelWidth={25}
                value={form.flat}
                onChange={(ev) => patch({ flat: ev.target.value })}
                inputWidth={50}
              />
              <Calendar
                label="Дата рождения"
                value={form.birthday}
                onChange={(val) => patch({ birthday: val })}
                maxDate={new Date()}
              />
              <div className={styles.sexRow}>
                <RegularText>Пол:</RegularText>
                <Radio
                  id="sex-1"
                  name="sex"
                  label="М"
                  checked={form.sex === "1"}
                  onChange={() => patch({ sex: "1" })}
                />
                <Radio
                  id="sex-2"
                  name="sex"
                  label="Ж"
                  checked={form.sex === "2"}
                  onChange={() => patch({ sex: "2" })}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={styles.clientFieldsGrid}>
            <Input
              label="Наименование"
              labelWidth={110}
              value={form.firstname}
              onChange={(ev) => patch({ firstname: ev.target.value })}
              inputWidth={200}
            />
            <Input
              label="ИНН"
              labelWidth={110}
              value={form.inn}
              onChange={(ev) => patch({ inn: ev.target.value })}
              inputWidth={200}
            />
            <Input
              label="КПП"
              labelWidth={110}
              value={form.kpp}
              onChange={(ev) => patch({ kpp: ev.target.value })}
              inputWidth={200}
            />
            <Input
              label="Р/С"
              labelWidth={110}
              value={form.bank}
              onChange={(ev) => patch({ bank: ev.target.value })}
              inputWidth={200}
            />
            <Input
              label="Адрес"
              labelWidth={110}
              value={form.address}
              onChange={(ev) => patch({ address: ev.target.value })}
              inputWidth={200}
            />
            <Input
              label="Телефон"
              labelWidth={110}
              value={form.phone}
              onChange={(ev) => patch({ phone: ev.target.value })}
              inputWidth={200}
            />
          </div>
        )}

        <VerticalSpace size={20} />
        <Divider />
        <VerticalSpace size={12} />

        {/* -------- Лицевой счёт -------- */}

        <Accordion
          withIcon
          filled
          disabled={!isEdit}
          collapsed={collapsed.discounts}
          handleCollapse={() => isEdit && toggle("discounts")}
          parent={<MediumText size={16}>Скидки</MediumText>}
        >
          {isEdit && (
            <>
              <CounterFieldsGrid
                fields={ACCOUNT_DISCOUNT_FIELDS}
                values={accountValues}
                onChange={onAccountFieldChange}
              />
              <VerticalSpace size={16} />
              <ClientDiscountsSection clientid={client?.clientid} />
            </>
          )}
        </Accordion>

        <VerticalSpace size={12} />

        <Accordion
          withIcon
          filled
          disabled={!isEdit}
          collapsed={collapsed.bonuses}
          handleCollapse={() => isEdit && toggle("bonuses")}
          parent={<MediumText size={16}>Бонусы</MediumText>}
        >
          {isEdit && (
            <>
              <CounterFieldsGrid
                fields={ACCOUNT_BONUS_FIELDS}
                values={accountValues}
                onChange={onAccountFieldChange}
              />
              <VerticalSpace size={16} />
              <ClientBonusOperations clientid={client?.clientid} />
            </>
          )}
        </Accordion>

        {isEdit && (
          <>
            <VerticalSpace size={12} />
            <Accordion
              withIcon
              filled
              collapsed={collapsed.transactions}
              handleCollapse={() => toggle("transactions")}
              parent={<MediumText size={16}>Операции по бонусам</MediumText>}
            >
              <ClientTransactionsSection clientid={client?.clientid} />
            </Accordion>
          </>
        )}

        <VerticalSpace size={12} />

        <Accordion
          withIcon
          filled
          collapsed={collapsed.marketing}
          handleCollapse={() => toggle("marketing")}
          parent={<MediumText size={16}>Маркетинг</MediumText>}
        >
          <div className={styles.marketingRow}>
            <Input
              label="Скидка"
              labelWidth={60}
              type="number"
              value={form.eachbouquetdiscount}
              onChange={(ev) => patch({ eachbouquetdiscount: ev.target.value })}
              inputWidth={80}
            />
            <RegularText>% на каждый</RegularText>
            <Input
              type="number"
              value={form.eachbouquet}
              onChange={(ev) => patch({ eachbouquet: ev.target.value })}
              inputWidth={80}
            />
            <RegularText>букет</RegularText>
          </div>
          <VerticalSpace size={12} />
          <div className={styles.marketingRow}>
            <RegularText>Бонусы сгорают после</RegularText>
            <Input
              type="number"
              value={form.defaultburnbonus}
              onChange={(ev) => patch({ defaultburnbonus: ev.target.value })}
              inputWidth={80}
            />
            <RegularText>дней после начисления</RegularText>
          </div>
          {isEdit && (
            <>
              <VerticalSpace size={16} />
              <ClientDatesSection clientid={client?.clientid} />
            </>
          )}
        </Accordion>

        <VerticalSpace size={12} />

        <Accordion
          withIcon
          filled
          collapsed={collapsed.info}
          handleCollapse={() => toggle("info")}
          parent={<MediumText size={16}>Дополнительная информация</MediumText>}
        >
          <div className={styles.extraInfo}>
            <Select
              label="Прайс лист"
              labelWidth={100}
              options={priceListOptions}
              value={priceListOption}
              onChange={setPriceListOption}
              isClearable
              isLoading={priceListsQuery.isLoading}
              width={280}
            />
            <VerticalSpace size={12} />
            <Input
              label="Комментарий"
              labelWidth={100}
              value={form.comment}
              onChange={(ev) => patch({ comment: ev.target.value })}
              inputWidth={280}
            />
          </div>
        </Accordion>
      </ModalBody>
      <ModalFooter>
        {!!error && (
          <RegularText size={13} color="danger" className={styles.footerError}>
            {error}
          </RegularText>
        )}
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
